import moment from "moment";
import { KeyValuePairInput, OrderDirectionType } from "../../__generated__/globalTypes";
import { GetReportQuery_report } from "../../views/App/__generated__/GetReportQuery";
import HESPFormLabel from "../HESPFormLabel/HESPFormLabel";
import { Button, Grid } from "@mui/material";
import KeyValueConstructor from "../../KeyValueConstructor/KeyValueConstructor";
import styled from "styled-components";
import * as React from "react";
import SchedulerChannelsSelector from "../SchedulersOverview/SchedulerChannelsSelector";
import { TOAST_SETTINGS } from "../BillingDetails/AddPaymentMethodDialog";
import { toast } from "react-toastify";
import { navigate } from "@reach/router";
import gql from "graphql-tag";
import { useMutation } from "react-apollo";
import { CreateReportMutation, CreateReportMutationVariables } from "./__generated__/CreateReportMutation";
import { UpdateReportMutation, UpdateReportMutationVariables } from "./__generated__/UpdateReportMutation";
import ReportBasicsForm from "./ReportBasicsForm";
import { GET_REPORTS_BASED_ON_PARAMS } from "./ReportsOverviewList";

const SectionTitle = styled.div`
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 15px;
`;

const ChannelsWrapper = styled.div`
    border-radius: 10px;
    padding: 10px 10px;
    background: #fafafa;
`;

interface Props {
    organizationId: string;
    report?: GetReportQuery_report;
}

export interface ReportObject {
    name: string;
    start: string;
    end: string;
    clientId: string;
    metadata: KeyValuePairInput[];
    channelIds: string[];
}

export const CREATE_REPORT = gql`
    mutation CreateReportMutation($input: CreateReportInput!) {
        createReport(input: $input) {
            reportId
        }
    }
`;

export const UPDATE_REPORT = gql`
    mutation UpdateReportMutation($input: UpdateReportInput!) {
        updateReport(input: $input) {
            reportId
        }
    }
`;

export default function ManageReportForm({ organizationId, report }: Props): JSX.Element {
    const isCreate = typeof report === "undefined";

    const [reportObj, setReportObj] = React.useState<ReportObject>(getInitialReportObj(report));
    const [firstSubmitDone, setFirstSubmitDone] = React.useState<boolean>(false);

    const [createReport, { loading: createLoading }] = useMutation<CreateReportMutation, CreateReportMutationVariables>(
        CREATE_REPORT,
    );
    const [updateReport, { loading: updateLoading }] = useMutation<UpdateReportMutation, UpdateReportMutationVariables>(
        UPDATE_REPORT,
    );

    function onAddKeyValueItem() {
        const currentItems = reportObj.metadata;
        const newItems = currentItems.concat([{ key: "", value: "" }]);
        setReportObj({
            ...reportObj,
            metadata: newItems,
        });
    }

    function onUpdateKeyValueItem(type: "key" | "value", value: string, index: number) {
        const currentItems = reportObj.metadata;
        currentItems[index] = {
            key: type === "key" ? value : currentItems[index].key,
            value: type === "value" ? value : currentItems[index].value,
        };
        setReportObj({
            ...reportObj,
            metadata: currentItems,
        });
    }

    function onRemoveKeyValueItem(index: number) {
        const currentItems = reportObj.metadata;
        const newItems = currentItems.filter((_item, i) => i !== index);
        setReportObj({
            ...reportObj,
            metadata: newItems,
        });
    }

    async function onSubmitClick() {
        setFirstSubmitDone(true);
        const { channelIds, end, name, start, clientId, metadata } = reportObj;
        if (
            validationPassed({
                reportObj,
            })
        ) {
            try {
                if (isCreate) {
                    await createReport({
                        variables: {
                            input: {
                                channelIds,
                                end,
                                name,
                                start,
                                organizationId,
                                clientId,
                                metadata,
                            },
                        },
                        refetchQueries: [
                            {
                                query: GET_REPORTS_BASED_ON_PARAMS,
                                variables: {
                                    limit: 20,
                                    orderBy: "start",
                                    orderDirection: OrderDirectionType.desc,
                                    organizationId: organizationId!,
                                    page: 1,
                                    end: moment().endOf("day"),
                                    start: moment("2024-08-01").startOf("day").toISOString(),
                                },
                            },
                        ],
                        awaitRefetchQueries: true,
                    });
                    navigate(`/app/${organizationId}/reporting`);
                } else {
                    await updateReport({
                        variables: {
                            input: {
                                channelIds,
                                organizationId,
                                end,
                                name,
                                reportId: report.reportId!,
                                clientId,
                                metadata,
                                start,
                            },
                        },
                        refetchQueries: [
                            {
                                query: GET_REPORTS_BASED_ON_PARAMS,
                                variables: {
                                    limit: 20,
                                    orderBy: "start",
                                    orderDirection: OrderDirectionType.desc,
                                    organizationId: organizationId!,
                                    page: 1,
                                    end: moment().endOf("day"),
                                    start: moment("2024-08-01").startOf("day").toISOString(),
                                },
                            },
                        ],
                        awaitRefetchQueries: true,
                    });
                    navigate(`/app/${organizationId}/reporting`);
                }
                toast.success(`Report successfully ${isCreate ? "created" : "updated"}! 🚀`, TOAST_SETTINGS);
            } catch (_e) {
                toast.error("Something went wrong 😞", TOAST_SETTINGS);
            }
        }
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <SectionTitle>Basic information</SectionTitle>
                <ReportBasicsForm
                    reportObj={reportObj}
                    initObj={getInitialReportObj(report)}
                    isCreate={isCreate}
                    isActive={report?.reportGenerated === true}
                    onChangeStart={(newValue: string) => {
                        setReportObj({
                            ...reportObj,
                            start: newValue,
                        });
                    }}
                    onChangeEnd={(newValue: string) =>
                        setReportObj({
                            ...reportObj,
                            end: newValue,
                        })
                    }
                    onChangeName={(newValue: string) => {
                        setReportObj({
                            ...reportObj,
                            name: newValue,
                        });
                    }}
                    onChangeClientId={(newValue: string) => {
                        setReportObj({
                            ...reportObj,
                            clientId: newValue,
                        });
                    }}
                    firstSubmitDone={firstSubmitDone}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <HESPFormLabel
                    label="Metadata"
                    description="Optional key value pairs to pass and connect to the scheduler"
                />
                <KeyValueConstructor
                    items={reportObj.metadata}
                    onAdd={onAddKeyValueItem}
                    onUpdate={(type, val, index) => onUpdateKeyValueItem(type, val, index)}
                    onRemove={(index: number) => onRemoveKeyValueItem(index)}
                />
            </Grid>
            <Grid item xs={12}>
                <ChannelsWrapper>
                    <SectionTitle>Connected channels</SectionTitle>
                    <SchedulerChannelsSelector
                        isActive={report?.reportGenerated === true}
                        organizationId={organizationId}
                        channelIds={reportObj.channelIds}
                        firstSubmitDone={firstSubmitDone}
                        onChannelsChange={(newChannelIds: string[]) =>
                            setReportObj({
                                ...reportObj,
                                channelIds: newChannelIds,
                            })
                        }
                    />
                </ChannelsWrapper>
            </Grid>
            <Grid item xs={12}>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "10px" }}>
                    <Button
                        onClick={onSubmitClick}
                        variant="contained"
                        size="large"
                        disabled={createLoading || updateLoading || !validationPassed({ reportObj })}
                    >
                        {createLoading || updateLoading
                            ? `${isCreate ? "Creating" : "Updating"} report...`
                            : `${isCreate ? "Create" : "Update"} report`}
                    </Button>
                </div>
            </Grid>
        </Grid>
    );

    function getInitialReportObj(report?: GetReportQuery_report): ReportObject {
        const isCreate = typeof report === "undefined";

        return {
            channelIds: isCreate ? [] : report.channelIds,
            clientId: isCreate ? "" : report.clientId ?? "",
            end: isCreate ? moment().subtract(1, "hours").toISOString() : moment(report.end).utc().toISOString(),
            start: isCreate ? moment().subtract(2, "hours").toISOString() : moment(report.start).utc().toISOString(),
            metadata: isCreate ? [] : report.metadata,
            name: isCreate ? "" : report.name,
        };
    }
}

interface ValidatorArgs {
    reportObj: ReportObject;
}

function validationPassed({ reportObj }: ValidatorArgs): boolean {
    const { channelIds, end, name, start, metadata } = reportObj;

    if (moment(start).isAfter(moment(end))) {
        return false;
    }

    if (name.trim().length === 0) {
        return false;
    }

    if (channelIds.length === 0) {
        return false;
    }

    if (metadata.length > 0) {
        const emptyKeys = metadata.map((m) => m.key).filter((k) => k.trim().length === 0);
        if (emptyKeys.length > 0) {
            return false;
        }
    }

    return true;
}
